import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { cases } from "../data/content";
import { CaseVisual } from "../components/visuals";
import { Expandable } from "../components/Expandable";
import { CopyLinkButton } from "../components/CopyLinkButton";
import { PrintButton } from "../components/PrintButton";

const Section = ({ section }) => (
  <div className="print-break">
    <h3 className="font-head text-lg font-semibold">{section.title}</h3>
    {section.body?.map((p) => (
      <p key={p} className="mt-2 leading-relaxed text-[var(--text-2)]">{p}</p>
    ))}
    {section.bullets && (
      <ul className="mt-3 space-y-2">
        {section.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2.5 text-[var(--text-2)]">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--accent)]" />
            <span className="leading-relaxed">{b}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function CasePage() {
  const { slug } = useParams();
  const c = cases[slug];
  if (!c) return <Navigate to="/work" replace />;

  return (
    <article className="mx-auto max-w-4xl px-5 py-10 sm:px-8 sm:py-14">
      <Link to="/work" className="ic-link no-print text-sm" data-testid="case-back-to-work">
        <ArrowLeft size={15} /> Back to more work
      </Link>

      {/* Above the fold */}
      <header className="mt-6">
        <div className="flex flex-wrap items-center gap-2">
          {c.tags.map((t) => (
            <span key={t} className="ic-tag">{t}</span>
          ))}
        </div>
        <h1 className="mt-4 font-head text-3xl font-bold tracking-tight sm:text-4xl" data-testid="case-title">{c.title}</h1>
        <p className="mt-3 max-w-3xl font-head text-xl font-semibold leading-snug text-[var(--accent)]">{c.headline}</p>
        <p className="mt-4 max-w-3xl leading-relaxed text-[var(--text-2)]"><span className="font-semibold text-[var(--text)]">Role — </span>{c.role}</p>
        <p className="mt-2 max-w-3xl leading-relaxed text-[var(--text-2)]"><span className="font-semibold text-[var(--text)]">Outcome — </span>{c.outcome}</p>
      </header>

      <div className="mt-8">
        <CaseVisual visual={c.visual} />
      </div>

      {c.relevance && (
        <p className="mt-5 rounded-xl border-l-2 border-[var(--accent)] bg-[var(--surface)] px-5 py-3 text-sm italic text-[var(--text-2)]">
          {c.relevance}
        </p>
      )}

      <div className="no-print mt-7 flex flex-wrap gap-3">
        <CopyLinkButton testid="case-copy-link" />
        <PrintButton label="Print this case" testid="case-print-button" />
      </div>

      {/* Optional detail */}
      <div className="mt-10 space-y-8">
        {c.sections.map((s) => (
          <Section key={s.title} section={s} />
        ))}
      </div>

      {/* Evidence (RSC) */}
      {c.evidence && (
        <div className="ic-surface mt-10 p-6 print-break" data-testid="case-evidence">
          <h3 className="font-head text-lg font-semibold">{c.evidence.heading}</h3>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {c.evidence.items.map((it) => (
              <div key={it.figure} className="rounded-xl border border-[var(--border)] bg-[var(--surface-2)] p-4">
                <p className="font-head text-2xl font-bold text-[var(--accent)]">{it.figure}</p>
                <p className="mt-1 text-sm leading-relaxed text-[var(--text-2)]">{it.label}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm leading-relaxed text-[var(--text-muted)]">{c.evidence.attribution}</p>
          <a href={c.evidence.source.href} target="_blank" rel="noopener noreferrer" className="ic-link mt-3 text-sm" data-testid="case-evidence-source">
            {c.evidence.source.label} <ArrowUpRight size={14} />
          </a>
        </div>
      )}

      {/* Related */}
      <div className="mt-12 border-t border-[var(--border)] pt-8">
        <p className="font-mono text-[0.62rem] uppercase tracking-wider text-[var(--text-muted)]">Related cases</p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {c.related.map((rslug) => (
            <Link
              key={rslug}
              to={`/work/${rslug}`}
              className="ic-surface flex items-center justify-between gap-3 p-4 transition-colors hover:border-[var(--border-accent)]"
              data-testid={`case-related-${rslug}`}
            >
              <span>
                <span className="font-mono text-[0.58rem] uppercase tracking-wider text-[var(--text-muted)]">{cases[rslug].name}</span>
                <span className="mt-1 block font-head text-sm font-semibold leading-snug">{cases[rslug].headline}</span>
              </span>
              <ArrowRight size={16} className="shrink-0 text-[var(--text-2)]" />
            </Link>
          ))}
        </div>
        <Link to="/" className="ic-link no-print mt-6 inline-flex text-sm" data-testid="case-back-to-interview">
          <ArrowLeft size={15} /> Back to the interview
        </Link>
      </div>
    </article>
  );
}
